import { nextCall, isNextCall, NextCall__symbol } from '@proc7ts/call-thru';
import { flatMapIt, flatMapArray, overOne } from './push-iterator.js';

/**
 * Creates a next call in {@link IterableCallChain | iterable call chain} that performs the next passes for each
 * element of the given iterable.
 *
 * This call passes elements to the next call on demand, while the `nextEach()` one transforms them all at once,
 * and iterates over results after that.
 *
 * @typeParam T - Iterated elements type.
 * @param iterable - An iterable containing elements to pass down the chain.
 *
 * @returns Next call for iterable call chain.
 */
function nextIterate(iterable) {
    return nextCall((chain, pass) => chain.iterate(pass, iterable));
}

function thruIt(it, ...passes) {
    const chain = (outcome, index) => {
        const lastPass = index >= passes.length;
        ++index;
        const pass = index < passes.length ? passes[index] : () => { };
        const handleResult = (outcome, callResult, arg) => {
            if (isNextCall(callResult)) {
                callResult[NextCall__symbol](chain(outcome, index), pass);
            }
            else if (lastPass) {
                outcome.push(overOne(arg));
            }
            else {
                chain(outcome, index).pass(pass, callResult);
            }
        };
        return ({
            call(fn, args) {
                handleResult(outcome, fn(...args), args);
            },
            pass(fn, arg) {
                handleResult(outcome, fn(arg), arg);
            },
            skip() { },
            iterate(fn, iterable) {
                outcome.push(flatMapIt(iterable, item => {
                    const itemOutcome = [];
                    handleResult(itemOutcome, fn(item), item);
                    return flatMapArray(itemOutcome);
                }));
            },
        });
    };
    const finalOutcome = [];
    chain(finalOutcome, 0).iterate(passes[0], it);
    return flatMapArray(finalOutcome);
}

export { nextIterate, thruIt };
//# sourceMappingURL=push-iterator.call-thru.js.map
